import { useCallback, useEffect, useReducer, useRef } from 'react';

import { ApiError, isCancelledUpload, type UploadProgress } from '../../lib/api.js';
import { newRequestKey } from '../conversations/hooks.js';
import { formatBytes, refuseFile } from '../references/format.js';

/** Three at a time: enough to fill a connection, few enough that one slow file does not starve the rest. */
export const UPLOAD_CONCURRENCY = 3;

export type UploadState = 'queued' | 'uploading' | 'done' | 'failed';

/** The server's own limit and allowlist (a `…/meta` answer) — never a number of ours. */
export interface UploadLimit {
  readonly maxBytes: number;
  readonly acceptedExtensions: readonly string[];
}

export interface QueuedUpload<M> {
  readonly key: string;
  readonly file: File;
  /**
   * Minted once per FILE and kept: a retry after a lost answer sends the same
   * key, so the server returns what it already made instead of a second one.
   */
  readonly clientRequestId: string;
  readonly state: UploadState;
  readonly loaded: number;
  readonly total: number;
  readonly error: string | null;
  /** What the server made from it (a reference, a file), once it answered. */
  readonly resultId: string | null;
  /**
   * What this file is sent with, fixed at its first attempt. A server that refuses
   * a different payload under the same key (409 `IDEMPOTENCY_KEY_REUSED`) must never
   * see a retry pick up a later change. Null until the caller starts it.
   */
  readonly metadata: M | null;
}

export interface RejectedFile {
  readonly key: string;
  readonly name: string;
  readonly reason: string;
}

export interface UploadSummary {
  readonly total: number;
  readonly done: number;
  readonly failed: number;
  readonly active: number;
  readonly queued: number;
}

export interface UploadControl {
  readonly signal: AbortSignal;
  readonly onProgress: (progress: UploadProgress) => void;
}

/** Sends one file; resolves with what the server made (its id), or rejects with the refusal. */
export type UploadSender<M> = (item: QueuedUpload<M>, control: UploadControl) => Promise<{ id: string } | null | undefined>;

/**
 * A BULK UPLOAD THAT KEEPS WHAT WORKED — the engine under the References library's
 * drawer and the Files tab.
 *
 * One request per file, three at a time, each with its own byte progress. A failure
 * never undoes a success: what was made stays made, the failed files are listed with
 * the server's reason, and Retry sends only those, under their original keys. Files
 * are checked against the server's published limit before anything is sent.
 *
 * Lives in the page rather than a drawer, so closing the drawer does not orphan
 * uploads in flight; leaving the page cancels what is still sending.
 */
export function useUploadQueue<M>(limit: UploadLimit | undefined, send: UploadSender<M>, onUploaded: () => void) {
  const items = useRef<QueuedUpload<M>[]>([]);
  const rejected = useRef<RejectedFile[]>([]);
  const active = useRef(0);
  const controllers = useRef(new Map<string, AbortController>());
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const uploadedRef = useRef(onUploaded);
  uploadedRef.current = onUploaded;
  const sendRef = useRef(send);
  sendRef.current = send;
  const limitRef = useRef(limit);
  limitRef.current = limit;

  useEffect(() => {
    const running = controllers.current;
    return () => {
      for (const controller of running.values()) controller.abort();
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<QueuedUpload<M>>) => {
    items.current = items.current.map((item) => (item.key === key ? { ...item, ...change } : item));
    rerender();
  }, []);

  const failureText = useCallback((error: unknown): string => {
    if (error instanceof ApiError) {
      // nginx's 413 carries no body; say the limit the server published, not a number of ours.
      if (error.code === 'FILE_TOO_LARGE' && limitRef.current) {
        return `Larger than the ${formatBytes(limitRef.current.maxBytes)} limit.`;
      }
      return error.message;
    }
    return 'The upload did not finish. Try again.';
  }, []);

  const run = useCallback(
    async (item: QueuedUpload<M>) => {
      const controller = new AbortController();
      controllers.current.set(item.key, controller);
      try {
        const created = await sendRef.current(item, {
          signal: controller.signal,
          onProgress: ({ loaded, total }) => {
            patch(item.key, { loaded, total });
          },
        });
        patch(item.key, {
          state: 'done',
          loaded: item.file.size,
          total: item.file.size,
          resultId: created?.id ?? null,
          error: null,
        });
        uploadedRef.current();
      } catch (error) {
        if (isCancelledUpload(error)) return;
        patch(item.key, { state: 'failed', error: failureText(error) });
      } finally {
        controllers.current.delete(item.key);
      }
    },
    [patch, failureText],
  );

  const pump = useCallback(() => {
    while (active.current < UPLOAD_CONCURRENCY) {
      const next = items.current.find((item) => item.state === 'queued' && item.metadata !== null);
      if (next === undefined) return;
      active.current += 1;
      patch(next.key, { state: 'uploading', loaded: 0, total: next.file.size, error: null });
      void run(next).finally(() => {
        active.current -= 1;
        pump();
      });
    }
  }, [patch, run]);

  /**
   * Checks each file against the server's limit; refused ones are listed and never
   * sent. With `metadata`, the accepted files start at once; without it they wait
   * for `start`.
   */
  const add = useCallback(
    (files: readonly File[], metadata?: M) => {
      const current = limitRef.current;
      for (const file of files) {
        const reason = current ? refuseFile(file, current) : null;
        const key = newRequestKey();
        if (reason !== null) {
          rejected.current = [...rejected.current, { key, name: file.name, reason }];
          continue;
        }
        items.current = [
          ...items.current,
          {
            key,
            file,
            clientRequestId: newRequestKey(),
            state: 'queued',
            loaded: 0,
            total: file.size,
            error: null,
            resultId: null,
            metadata: metadata ?? null,
          },
        ];
      }
      rerender();
      if (metadata !== undefined) pump();
    },
    [pump],
  );

  /** Starts every file not yet sent, with these shared fields. */
  const start = useCallback(
    (metadata: M) => {
      items.current = items.current.map((item) =>
        item.state === 'queued' && item.metadata === null ? { ...item, metadata } : item,
      );
      pump();
    },
    [pump],
  );

  /** Sends the failed files again under their original keys and fields. */
  const retryFailed = useCallback(() => {
    items.current = items.current.map((item) =>
      item.state === 'failed' ? { ...item, state: 'queued', error: null, loaded: 0 } : item,
    );
    pump();
  }, [pump]);

  /** A file not yet sending leaves the list. */
  const remove = useCallback((key: string) => {
    items.current = items.current.filter((item) => item.key !== key || item.state === 'uploading');
    rejected.current = rejected.current.filter((r) => r.key !== key);
    rerender();
  }, []);

  /** Clears the finished list once nothing is sending; false (and nothing cleared) while something is. */
  const reset = useCallback((): boolean => {
    if (active.current > 0) return false;
    items.current = [];
    rejected.current = [];
    rerender();
    return true;
  }, []);

  const list = items.current;
  const summary: UploadSummary = {
    total: list.length,
    done: list.filter((i) => i.state === 'done').length,
    failed: list.filter((i) => i.state === 'failed').length,
    active: list.filter((i) => i.state === 'uploading').length,
    queued: list.filter((i) => i.state === 'queued').length,
  };
  const started = list.some((i) => i.metadata !== null);

  return { items: list, rejected: rejected.current, summary, started, add, start, retryFailed, remove, reset };
}

/** "2 added · 1 uploading · 1 failed" — what a batch has done so far, in one line. */
export function uploadSummaryText(summary: UploadSummary, doneWord = 'added'): string {
  const parts: string[] = [];
  if (summary.done > 0) parts.push(`${String(summary.done)} ${doneWord}`);
  if (summary.active > 0) parts.push(`${String(summary.active)} uploading`);
  if (summary.queued > 0) parts.push(`${String(summary.queued)} waiting`);
  if (summary.failed > 0) parts.push(`${String(summary.failed)} failed`);
  return parts.length === 0 ? '' : parts.join(' · ');
}
